// src/careComponents/helper/referralHelpers.ts
// Pure utilities: no JSX here.
import type { ChipProps } from "@mui/material";

/* =============================================================================
   JSON helpers (tolerant)
   ========================================================================== */

/**
 * Safely parse JSON or pass through objects. Returns null on failure.
 */
export function parseJSONSafe<T = any>(raw: any): T | null {
  if (!raw) return null;
  try {
    return typeof raw === "string" ? (JSON.parse(raw) as T) : (raw as T);
  } catch {
    return null;
  }
}

/**
 * Try to read a value from 'extra' even if JSON is malformed.
 * - First attempts JSON.parse (or uses object directly)
 * - Otherwise, regex-scan the raw string for "<key>":"value"
 *   (basic string value support; for numbers see per-key helpers)
 */
export function readFromExtra(raw: any, keys: string[]): any {
  // JSON/Object path
  const asObj = parseJSONSafe<any>(raw) ?? (typeof raw === "object" ? raw : null);
  if (asObj && typeof asObj === "object") {
    for (const key of keys) {
      // support dotted paths (e.g., "installmentPlan.items")
      const path = key.split(".");
      let cur: any = asObj;
      let ok = true;
      for (const part of path) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, part)) {
          cur = cur[part];
        } else {
          ok = false;
          break;
        }
      }
      if (ok && cur != null) return cur;
    }
  }

  // Regex fallback for strings like: ..."patientInitials":"ABCD"...
  if (typeof raw === "string") {
    for (const key of keys) {
      const re = new RegExp(`"${key}"\\s*:\\s*"(.*?)"`, "i");
      const m = raw.match(re);
      if (m && m[1] != null) return m[1];
    }
  }

  return undefined;
}

/* =============================================================================
   Date helpers (no JSX here)
   ========================================================================== */

export function isDateOnlyString(s?: string): boolean {
  return !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
}
export function coerceToDate(input: any): Date | null {
  if (input == null) return null;
  if (input instanceof Date && !Number.isNaN(input.getTime())) return input;

  if (typeof input === "number") {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof input === "string") {
    const s = input.trim();
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      const ms = s.length <= 10 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (isDateOnlyString(s)) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/** Legacy helper kept for other call sites */
export function buildResidentLine(name?: string | null, key?: string | null): string {
  if (name && key) return `${name} (${key})`;
  if (name) return name;
  if (key) return key;
  return "—";
}

/**
 * Best-available resident label:
 * 1) lead.residentName
 * 2) lead.residentKey
 * 3) extra.patientInitials / extra.residentInitials / extra.patient
 */
export function getResidentLabel(lead: any): string {
  const name: string | undefined = lead?.residentName ?? undefined;
  const key: string | undefined = lead?.residentKey ?? undefined;
  if (name) return name;
  if (key) return key;

  const initials =
    readFromExtra(lead?.extra, ["patientInitials", "residentInitials", "patient"]) ??
    undefined;

  return (typeof initials === "string" && initials.trim()) ? initials.trim() : "—";
}

type UrgencyChoice = "ASAP" | "WEEK" | "MONTH" | "DATE";
/* ---------------- Urgency / Payer / Kind ---------------- */

export function mapUrgency(value?: string | null): string {
  switch ((value || "").toUpperCase()) {
    case "IMMEDIATE": return "Immediate";
    case "WEEK": return "Within a Week";
    case "MONTH": return "Within a Month";
    case "UNKNOWN": return "Unknown";
    default: return value || "—";
  }
}

export function mapPayerType(value?: string | null): string {
  switch ((value || "").toUpperCase()) {
    case "PRIVATE": return "Private Pay";
    case "MEDICAID": return "Medicaid";
    case "MEDICARE": return "Medicare";
    case "INSURANCE": return "Insurance";
    default: return value || "—";
  }
}

export function mapKind(value?: string | null): string {
  switch ((value || "").toUpperCase()) {
    case "AVAILABILITY": return "Availability Request";
    case "REFERRAL": return "Referral";
    case "WAITLIST": return "Waitlist";
    default: return value || "—";
  }
}

/* =============================================================================
   Care Need normalization
   ========================================================================== */

function normalizeCareNeed(value?: string | null): string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const k = raw.toLowerCase().replace(/[_-]/g, " ").replace(/\s+/g, " ");

  const map: Record<string, string> = {
    "memory care": "MEMORY_CARE",
    "alzheimers": "MEMORY_CARE",
    "alzheimer": "MEMORY_CARE",
    "dementia": "DEMENTIA",
    "respite": "RESPITE",
    "hospice": "HOSPICE",
    "physical disability": "PHYSICAL_DISABILITY",
    "physical disabilities": "PHYSICAL_DISABILITY",
    "mental health": "MENTAL_HEALTH",
  };

  if (map[k]) return map[k];

  const upper = raw.toUpperCase();
  if (["MEMORY_CARE", "DEMENTIA", "RESPITE", "HOSPICE", "PHYSICAL_DISABILITY", "MENTAL_HEALTH"].includes(upper)) {
    return upper;
  }
  return raw; // unknown string → pass through
}

/**
 * Get raw care-need from:
 * - lead.careNeed
 * - lead.careLevelRequested
 * - extra.careNeed
 * - extra.care_level_requested
 */
export function getCareNeedRaw(lead: any): string | null {
  const src: any =
    lead?.careNeed ??
    lead?.careLevelRequested ??
    readFromExtra(lead?.extra, ["careNeed", "care_level_requested"]);

  return normalizeCareNeed(src);
}

export function mapCareNeed(value?: string | null): string {
  if (!value) return "—";
  const v = String(value);
  switch (v.toUpperCase()) {
    case "MEMORY_CARE": return "Memory Care";
    case "DEMENTIA": return "Dementia";
    case "RESPITE": return "Respite";
    case "HOSPICE": return "Hospice";
    case "PHYSICAL_DISABILITY": return "Physical Disability";
    case "MENTAL_HEALTH": return "Mental Health";
    default:
      return v.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/* =============================================================================
   Settlement / pricing helpers
   ========================================================================== */

export type SettlementMeta = {
  holdExpiresAt?: string | null;
  settledAt?: string | null;
  feePerBed?: number | null;
  bedCount?: number | null;
};

export function getSettlementMeta(extra: any): SettlementMeta {
  const e = parseJSONSafe<any>(extra) ?? (typeof extra === "object" ? extra : {});
  return {
    holdExpiresAt: e?.holdExpiresAt ?? null,
    settledAt: e?.settledAt ?? null,
    feePerBed: e?.feePerBed ?? null,
    bedCount: e?.bedCount ?? null,
  };
}

export type PricingFlags = {
  provided: boolean;
  mode: "SELF_SERVE" | "CONCIERGE";
  isRush: boolean;
};

export function getPricingFlagsFromExtra(extraField: any): PricingFlags {
  const ex = parseJSONSafe<any>(extraField) || (typeof extraField === "object" ? extraField : {});
  const raw = ex?.pricing || {};

  let mode: PricingFlags["mode"] = raw?.mode === "CONCIERGE" ? "CONCIERGE" : "SELF_SERVE";
  let isRush = mode === "CONCIERGE" && !!raw?.isRush;

  if (!ex?.pricing) {
    if (ex?.pricingMode === "CONCIERGE") mode = "CONCIERGE";
    if (typeof ex?.pricingRush === "boolean") isRush = mode === "CONCIERGE" && !!ex.pricingRush;
  }

  return { provided: !!ex?.pricing || !!ex?.pricingMode, mode, isRush };
}

/* ---------- Referrer external payment total ---------- */

/**
 * Total amount the referrer says they already paid externally.
 * Looks for `extra.referrerPaidTotal` (number or string) and
 * also supports messy JSON strings via regex.
 */
export function getReferrerPaidTotal(lead: any): number {
  if (!lead) return 0;

  // If it's already an object or well-formed JSON string
  const ex = parseJSONSafe<any>(lead?.extra) ?? (typeof lead?.extra === "object" ? lead.extra : null);
  if (ex && ex.referrerPaidTotal != null) {
    const n = Number(ex.referrerPaidTotal);
    return Number.isFinite(n) ? n : 0;
  }

  // Last resort: regex numeric extraction from a raw JSON-like string
  if (typeof lead?.extra === "string") {
    const m = lead.extra.match(/"referrerPaidTotal"\s*:\s*([0-9]+(?:\.[0-9]+)?)/i);
    if (m && m[1] != null) {
      const n = Number(m[1]);
      return Number.isFinite(n) ? n : 0;
    }
  }

  return 0;
}

/* ---------- Installment summary (to show Partial vs Paid) ---------- */

export type InstallmentSummary = {
  count: number;
  paidCount: number;
  totalAmount: number;
  paidAmount: number;
  status: "NONE" | "PARTIAL" | "PAID";
};

/**
 * Reads installments from a few likely shapes:
 * - extra.installments: Array<...>
 * - extra.installmentPlan.items: Array<...>
 * - extra.installmentItems: Array<...>
 */
export function getInstallmentSummaryFromExtra(extraField: any): InstallmentSummary {
  const ex = parseJSONSafe<any>(extraField) || (typeof extraField === "object" ? extraField : {});
  const items: any[] | undefined =
    Array.isArray(ex?.installments) ? ex.installments
      : Array.isArray(ex?.installmentItems) ? ex.installmentItems
        : Array.isArray(ex?.installmentPlan?.items) ? ex.installmentPlan.items
          : undefined;

  if (!Array.isArray(items) || items.length === 0) {
    return { count: 0, paidCount: 0, totalAmount: 0, paidAmount: 0, status: "NONE" };
  }

  let count = 0, paidCount = 0, totalAmount = 0, paidAmount = 0;

  for (const it of items) {
    const amt = Number(it?.amount ?? it?.amountDue ?? it?.total ?? 0) || 0;
    const paidAmtRaw = Number(it?.paidAmount ?? it?.amountPaid ?? 0) || 0;
    const paidFlag = !!(it?.paid || it?.isPaid || it?.paidAt || it?.settledAt);

    count += 1;
    totalAmount += amt;

    // If a paid flag is present, treat as fully paid regardless of partial amounts
    const thisPaid = paidFlag ? Math.max(paidAmtRaw, amt) : paidAmtRaw;
    paidAmount += thisPaid;

    if (paidFlag || thisPaid >= (amt - 0.005)) paidCount += 1;
  }

  // Tolerate rounding (e.g., pennies)
  const fullyPaid = paidCount === count && totalAmount > 0 && Math.abs(paidAmount - totalAmount) <= 0.01;
  const status: InstallmentSummary["status"] =
    fullyPaid ? "PAID" : paidAmount > 0 ? "PARTIAL" : "NONE";

  return { count, paidCount, totalAmount, paidAmount, status };
}

/* =============================================================================
   Formatting
   ========================================================================== */

export function formatUSD(amount: number): string {
  if (!Number.isFinite(amount)) return "$0.00";
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
